/**
 * Embedded-mode SQLite access helpers.
 *
 * `getEmbeddedDatabase` — returns an open sql.js Database for the current
 * embedded SQLite path (the path Unity last delivered via `setSqlitePath`).
 * Each call fetches and opens a fresh Database instance so the caller gets
 * a consistent read-only snapshot without managing connection lifetime.
 *
 * `peekAudioBytes` — reads the first `n` bytes of an audio clip's
 * `audio_data` BLOB from the embedded SQLite database via sql.js.
 * Used by the embedded export orchestrator (Task 9) to detect audio format
 * on the web side without loading the full clip.
 *
 * `setEmbeddedSqlitePath` — called by sqliteBridge when Unity delivers a
 * new SQLite path via the `setSqlitePath` bridge message.
 */

import type { Database } from 'sql.js';
import { openDatabase } from './sqliteService.ts';

// ── Module-level state ────────────────────────────────────────────────────

let _embeddedSqlitePath: string | null = null;

/**
 * Record the current embedded SQLite path so later `getEmbeddedDatabase`
 * calls know where to fetch. Called by sqliteBridge immediately before
 * (or after) loadPrevisFile so the path is always in sync with the DB.
 */
export function setEmbeddedSqlitePath(path: string | null): void {
  _embeddedSqlitePath = path;
}

// ── Database accessor ─────────────────────────────────────────────────────

/**
 * Opens and returns a sql.js Database for the current embedded SQLite path.
 *
 * Each call fetches the file and opens a fresh Database; the caller is
 * responsible for calling `db.close()` when done if memory is a concern.
 * For the audio-peeker use-case (~512 bytes read + close), this is fine.
 *
 * Throws if no embedded path has been set (i.e. not running in embedded mode
 * or `setSqlitePath` has not been received yet).
 */
export async function getEmbeddedDatabase(): Promise<Database> {
  if (!_embeddedSqlitePath) {
    throw new Error(
      '[sqlReader] No embedded SQLite path set. ' +
      'Ensure setEmbeddedSqlitePath() is called before getEmbeddedDatabase().',
    );
  }

  // Cache-bust the WKWebView file cache (same approach as sqliteBridge.ts)
  const url = _embeddedSqlitePath + (_embeddedSqlitePath.includes('?') ? '&' : '?') + '_t=' + Date.now();
  const response = await fetch(url);
  // file:// URLs return status 0 with ok=false — the body is still readable
  if (!response.ok && response.status !== 0) {
    throw new Error(`[sqlReader] Failed to fetch SQLite: ${response.status}`);
  }
  const buffer = await response.arrayBuffer();
  return openDatabase(new Uint8Array(buffer));
}

// ── Audio peeker ──────────────────────────────────────────────────────────

/**
 * Reads the first `n` bytes of audiodata for the given clip from a sql.js
 * Database. Uses SQL substr() so the cross-WASM transfer stays small (~512
 * bytes per clip), which is enough for magic-byte format and channel detection.
 *
 * Returns null when the clip row is not found.
 *
 * Takes the Database as a parameter so the caller (the embedded export
 * orchestrator) opens a single DB at the start of the export, runs all peek
 * queries against it, and closes it once at the end. This avoids fetch-per-clip
 * cost and DB-leaks.
 *
 * NOTE: column names are `audiodata` (the BLOB, no underscore) and
 * `audioclipid` (the primary key, not `id`) per the schema actually used
 * by Unity's DatabaseManager — earlier prose specs called these `audio_data`
 * and `id`, both wrong.
 */
export function peekAudioBytes(db: Database, clipId: number, n: number): Uint8Array | null {
  const result = db.exec(
    'SELECT substr(audiodata, 1, ?) AS head FROM timeline_audioclips WHERE audioclipid = ?',
    [n, clipId],
  );
  const row = result[0]?.values[0];
  if (!row) return null;
  const head = row[0];
  if (head instanceof Uint8Array) return head;
  if (head == null) return null;
  // Defensive: sql.js may return BLOBs as ArrayBuffer or Uint8Array depending on configuration.
  return new Uint8Array(head as ArrayBuffer);
}

/**
 * Reads the full audiodata blob for the given clip from a sql.js Database.
 * Used by HTML export in embedded mode where audio bytes live in SQLite
 * rather than the in-memory audioBlobStore.
 */
export function readAudioBytes(db: Database, clipId: number): Uint8Array | null {
  const result = db.exec(
    'SELECT audiodata FROM timeline_audioclips WHERE audioclipid = ?',
    [clipId],
  );
  const row = result[0]?.values[0];
  if (!row) return null;
  const data = row[0];
  if (data instanceof Uint8Array) return data;
  if (data == null) return null;
  return new Uint8Array(data as ArrayBuffer);
}
